import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Grid,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import {
  getFunctionsByCategory,
  searchFunctions,
  evaluateFunction,
  parseFunctionArgs,
  toRequestFunctionReference,
  FunctionDefinition,
  primeCustomFunctionsCache,
  invalidateCustomFunctionsCache,
  CUSTOM_FUNCTIONS_CHANGED_EVENT,
  MAX_CUSTOM_FUNCTIONS_PER_USER,
} from '../services/functionService';
import { apiService } from '../services/apiService';
import { DFT_BRAND, zoomedMainMinHeight } from '../styles/brandPalette';

type CustomFunctionDto = {
  id: number;
  name: string;
  type?: string;
  code?: string | null;
  value?: string | null;
  version: number;
  isActive: boolean;
};

const FUNCTIONS_CARD_RADIUS = '14px';
const functionsPageSx = {
  width: '100%',
  maxWidth: 'none',
  minWidth: 0,
  alignSelf: 'stretch',
  flex: 1,
  px: 0,
  py: 0,
  bgcolor: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
  boxSizing: 'border-box' as const,
  minHeight: {
    xs: 'calc(100dvh - 64px)',
    ...zoomedMainMinHeight(),
  },
  '& .MuiCard-root': {
    borderRadius: `${FUNCTIONS_CARD_RADIUS} !important`,
    overflow: 'hidden',
  },
  '& .MuiAlert-root': {
    borderRadius: '12px',
  },
  '& .MuiTypography-body2': {
    fontSize: '0.875rem',
    lineHeight: 1.45,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
  },
  '& .MuiAccordion-root': {
    borderRadius: `${FUNCTIONS_CARD_RADIUS} !important`,
    boxShadow: 'none',
    border: `1px solid ${DFT_BRAND.line}`,
    overflow: 'hidden',
    '&:before': { display: 'none' },
  },
} as const;
const FUNCTIONS_WORKSPACE_SX = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '0 12px 12px 12px',
  border: `1px solid ${DFT_BRAND.line}`,
  borderTop: `1px solid ${DFT_BRAND.line}`,
  bgcolor: DFT_BRAND.bg,
  boxShadow: 'none',
  overflow: 'visible',
  p: { xs: 1.25, sm: 1.5 },
} as const;
const functionsCardSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden',
  position: 'relative' as const,
  border: `1px solid ${DFT_BRAND.line}`,
  borderRadius: `${FUNCTIONS_CARD_RADIUS} !important`,
  boxShadow: '0 1px 2px rgba(23, 36, 65, 0.04)',
  bgcolor: DFT_BRAND.paper,
} as const;
const functionsBtnSx = {
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.875rem',
  height: 36,
  minHeight: 36,
  whiteSpace: 'nowrap' as const,
  boxShadow: 'none',
};
const functionCodeLabelSx = {
  color: DFT_BRAND.muted,
  fontWeight: 600,
  fontSize: '0.8125rem',
  lineHeight: 1.4,
} as const;
const functionCodeValueSx = {
  px: 1.5,
  py: 1.25,
  fontFamily: 'monospace',
  fontSize: '0.9375rem',
  lineHeight: 1.5,
  mt: 0.5,
  borderRadius: '10px',
  overflow: 'hidden',
  wordBreak: 'break-word' as const,
  whiteSpace: 'pre-wrap' as const,
} as const;
const FUNCTIONS_DIALOG_SX = {
  '& .MuiDialog-container': {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 2,
    paddingBottom: 2,
    boxSizing: 'border-box',
  },
} as const;
const FUNCTIONS_DIALOG_PAPER_SX = {
  width: { xs: 'calc(100% - 32px)', sm: 440 },
  maxWidth: 'min(92vw, 520px)',
  maxHeight: 'calc(100vh - 160px)',
  m: 2,
  borderRadius: '14px',
  border: `1px solid ${DFT_BRAND.line}`,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
} as const;

/** Create-function dialog sits at modal+2; menus must be above that layer. */
const FUNCTION_CREATE_DIALOG_Z = (theme: Theme) => theme.zIndex.modal + 2;

/**
 * MUI Select menus portal to document.body at theme.zIndex.modal (1300) by default.
 * Our dialog uses modal+2, so the Popover *root* (MenuProps.sx) must be higher — PaperProps alone is not enough.
 */
const FUNCTION_DIALOG_SELECT_MENU_PROPS = {
  disableScrollLock: true,
  sx: { zIndex: (theme: Theme) => theme.zIndex.modal + 10 },
  PaperProps: {
    sx: {
      zIndex: (theme: Theme) => theme.zIndex.modal + 10,
      maxHeight: 280,
    },
  },
  MenuListProps: {
    sx: { maxHeight: 260, overflowY: 'auto' as const },
  },
};

function mergeSavedCustomFunction(
  prev: CustomFunctionDto[],
  saved: CustomFunctionDto,
): CustomFunctionDto[] {
  const next = prev.filter((f) => f.id !== saved.id);
  next.push(saved);
  next.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return next;
}

function findCustomByName(list: CustomFunctionDto[], name: string): CustomFunctionDto | undefined {
  const lower = name.toLowerCase();
  return list.find((f) => f.name.toLowerCase() === lower);
}

/** Default Play/example args based on how the JS body uses args[]. */
function inferDefaultTestArgs(code: string | null | undefined): unknown[] {
  if (!code) return ['hello'];
  if (/JSON\.parse/i.test(code)) {
    return ['{"name":"Alice"}'];
  }
  // Numeric usage: Array(n), Math.*, arithmetic — use numbers, not "hello"
  const looksNumeric =
    /Array\s*\(\s*args\[\d+\]/.test(code) ||
    /Math\.\w+\s*\([^)]*args\[\d+\]/.test(code) ||
    /args\[\d+\]\s*[\*\+\-\/%]/.test(code) ||
    /[\*\+\-\/%]\s*args\[\d+\]/.test(code) ||
    /Number\s*\(\s*args\[\d+\]/.test(code) ||
    /parseInt\s*\(\s*args\[\d+\]/.test(code) ||
    /parseFloat\s*\(\s*args\[\d+\]/.test(code);

  if (/args\[1\]/.test(code)) {
    return looksNumeric ? [5, 100] : ['hello', 'world'];
  }
  if (/args\[0\]/.test(code)) {
    return looksNumeric ? [5] : ['hello'];
  }
  return [];
}

const Functions: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | false>('Dynamic Variables');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [testResultDialog, setTestResultDialog] = useState<{ name: string; result: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState<CustomFunctionDto | null>(null);
  const [savingFunction, setSavingFunction] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [fnType, setFnType] = useState<'STATIC' | 'JS'>('STATIC');
  const [valueType, setValueType] = useState<'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON'>('STRING');
  const [staticValue, setStaticValue] = useState('');
  const [jsCode, setJsCode] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [customFunctions, setCustomFunctions] = useState<CustomFunctionDto[]>([]);
  const [customFunctionLimit, setCustomFunctionLimit] = useState(MAX_CUSTOM_FUNCTIONS_PER_USER);
  const [customFunctionsLoadError, setCustomFunctionsLoadError] = useState<string | null>(null);
  const [customFunctionsLoading, setCustomFunctionsLoading] = useState(false);
  const [createDialogError, setCreateDialogError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FunctionDefinition | null>(null);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const customFunctionsLoadedOnce = useRef(false);

  const fetchCustomFunctions = async (options?: { background?: boolean }) => {
    const background = options?.background ?? false;
    try {
      if (!background) {
        setCustomFunctionsLoading(true);
      }
      setCustomFunctionsLoadError(null);
      const res = await apiService.get('/functions');
      const raw = res?.data;
      let list: CustomFunctionDto[] = [];
      let maxAllowed = MAX_CUSTOM_FUNCTIONS_PER_USER;
      if (Array.isArray(raw)) {
        list = raw as CustomFunctionDto[];
      } else if (raw && typeof raw === 'object' && Array.isArray((raw as { functions?: unknown }).functions)) {
        const payload = raw as { functions: CustomFunctionDto[]; maxAllowed?: number };
        list = payload.functions;
        maxAllowed = payload.maxAllowed ?? MAX_CUSTOM_FUNCTIONS_PER_USER;
      } else if (raw?.error) {
        if (!background || !customFunctionsLoadedOnce.current) {
          setCustomFunctions([]);
        }
        setCustomFunctionsLoadError(String(raw.error));
        return;
      } else {
        if (!background || !customFunctionsLoadedOnce.current) {
          setCustomFunctions([]);
        }
        setCustomFunctionsLoadError('Could not load custom functions (unexpected server response)');
        return;
      }
      setCustomFunctions(list);
      setCustomFunctionLimit(maxAllowed);
      primeCustomFunctionsCache(list);
      customFunctionsLoadedOnce.current = true;
    } catch (err: any) {
      if (!background || !customFunctionsLoadedOnce.current) {
        setCustomFunctions([]);
      }
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load custom functions';
      setCustomFunctionsLoadError(typeof msg === 'string' ? msg : 'Failed to load custom functions');
    } finally {
      if (!background) {
        setCustomFunctionsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchCustomFunctions();
    const onCustomFunctionsChanged = () => {
      setExpandedCategory('Custom');
      void fetchCustomFunctions({ background: true });
    };
    window.addEventListener(CUSTOM_FUNCTIONS_CHANGED_EVENT, onCustomFunctionsChanged);
    return () => window.removeEventListener(CUSTOM_FUNCTIONS_CHANGED_EVENT, onCustomFunctionsChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const functionsByCategory = useMemo(() => {
    const builtIn = getFunctionsByCategory();
    const customDefs: FunctionDefinition[] = (customFunctions || []).map((f) => {
      const isStatic = (f.type || 'JS').toUpperCase() === 'STATIC';
      let preview = '';
      if (isStatic && f.value != null) {
        try {
          preview = JSON.stringify(JSON.parse(f.value));
        } catch {
          preview = f.value;
        }
      }
      // Best-effort preview for simple JS functions like: return "hello";
      const jsPreviewMatch = !isStatic && f.code
        ? f.code.match(/return\s+["'`]([^"'`]+)["'`]\s*;?/)
        : null;
      const jsPreview = jsPreviewMatch?.[1] ?? '';
      const defaultArgs =
        !isStatic && f.code ? (inferDefaultTestArgs(f.code) as Array<string | number>) : [];
      const argPreview =
        defaultArgs.length > 0
          ? defaultArgs.map((a) => JSON.stringify(a)).join(', ')
          : '';
      return {
        name: `$${f.name}`,
        category: 'Custom',
        description: isStatic
          ? `Static value (type: ${f.type || 'STATIC'})`
          : 'Custom JavaScript function (stored on server)',
        syntax: isStatic ? `$[${f.name}]` : `$[${f.name}](...args)`,
        example: isStatic
          ? `$[${f.name}] → ${preview.slice(0, 120)}${preview.length > 120 ? '…' : ''}`
          : jsPreview
            ? `$[${f.name}](${argPreview}) → ${jsPreview}`
            : argPreview
              ? `$[${f.name}](${argPreview})`
              : `$[${f.name}]()`,
        implementation: () => '',
      };
    });
    return {
      ...(Object.keys(builtIn).length ? builtIn : {}),
      Custom: customDefs,
    };
  }, [customFunctions]);

  const filteredFunctions = useMemo(() => {
    if (!searchQuery.trim()) {
      return functionsByCategory;
    }

    const q = searchQuery.toLowerCase();
    const results = searchFunctions(searchQuery);
    const grouped: Record<string, FunctionDefinition[]> = {};

    results.forEach(func => {
      if (!grouped[func.category]) {
        grouped[func.category] = [];
      }
      grouped[func.category].push(func);
    });

    const customList = functionsByCategory['Custom'] || [];
    const customMatch = customList.filter(
      f =>
        f.name.toLowerCase().includes(q) ||
        f.description.toLowerCase().includes(q) ||
        f.syntax.toLowerCase().includes(q)
    );
    if (customMatch.length) {
      grouped['Custom'] = customMatch;
    }

    return grouped;
  }, [searchQuery, functionsByCategory]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({
      open: true,
      message: `${label} copied to clipboard!`,
      severity: 'success',
    });
  };

  /** Copy request-reference syntax $[name], never bare $name. */
  const handleCopyFunctionReference = (func: FunctionDefinition) => {
    handleCopy(toRequestFunctionReference(func.name), 'Function reference');
  };

  const handleTest = async (func: FunctionDefinition) => {
    try {
      const callMatch = func.example.match(/\$\[([A-Za-z][A-Za-z0-9_]*)\](?:\(([\s\S]*?)\))?|\$([A-Za-z][A-Za-z0-9_]*)(?:\(([\s\S]*?)\))?/);
      const exampleArgsRaw = callMatch?.[2] ?? callMatch?.[4];
      let args: unknown[] =
        exampleArgsRaw !== undefined && exampleArgsRaw.trim() !== ''
          ? parseFunctionArgs(exampleArgsRaw)
          : [];

      let result: string;

      if (func.category === 'Custom') {
        const customName = func.name.startsWith('$') ? func.name.substring(1) : func.name;
        const custom = findCustomByName(customFunctions || [], customName);
        if (!custom) {
          throw new Error(`Custom function "${customName}" not found in server list`);
        }

        if (!args.length) {
          args = inferDefaultTestArgs(custom.code);
        }

        result = await evaluateFunction(func.name, ...args);
      } else {
        result = await evaluateFunction(func.name, ...args);
      }

      setTestResultDialog({ name: func.name, result });
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      setSnackbar({
        open: true,
        message: `Error: ${typeof serverMessage === 'string' ? serverMessage : 'Function test failed'}`,
        severity: 'error',
      });
    }
  };

  const handleAccordionChange = (category: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategory(isExpanded ? category : false);
  };

  const getCategoryColor = (category: string): string => {
    const colors: Record<string, string> = {
      'Dynamic Variables': '#1976d2',
      'Date/Time': '#2e7d32',
      'String': '#ed6c02',
      'Encoding': '#9c27b0',
      'Hashing': '#d32f2f',
      'Math': '#0288d1',
      'Fake Data': '#7b1fa2',
      'Utility': '#616161',
      'Custom': '#6a1b9a',
    };
    return colors[category] || '#757575';
  };

  const buildStaticPayloadValue = (): string => {
    if (valueType === 'STRING') {
      return JSON.stringify(staticValue);
    }
    if (valueType === 'NUMBER') {
      const n = Number(staticValue);
      if (!Number.isFinite(n)) {
        throw new Error('Enter a valid number');
      }
      return n.toString();
    }
    if (valueType === 'BOOLEAN') {
      return staticValue === 'true' ? 'true' : 'false';
    }
    const t = staticValue.trim();
    JSON.parse(t);
    return t;
  };

  const handleGenerateCode = async () => {
    const trimmedPrompt = aiPrompt.trim();
    if (!trimmedPrompt) {
      setSnackbar({ open: true, message: 'Describe what the function should do', severity: 'error' });
      return;
    }
    try {
      setGeneratingCode(true);
      const res = await apiService.post('/functions/generate-code', {
        prompt: trimmedPrompt,
        functionName: newName.trim() || undefined,
      }, { timeout: 300000 });
      const code = res?.data?.code;
      if (!code || typeof code !== 'string') {
        throw new Error('AI did not return function code');
      }
      setJsCode(code);
      setFnType('JS');
      setSnackbar({ open: true, message: 'Function code generated — review and Save', severity: 'success' });
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      setSnackbar({
        open: true,
        message: typeof serverMessage === 'string' ? serverMessage : 'Failed to generate code',
        severity: 'error',
      });
    } finally {
      setGeneratingCode(false);
    }
  };

  const resetFunctionForm = () => {
    setEditingFunction(null);
    setNewName('');
    setFnType('STATIC');
    setValueType('STRING');
    setStaticValue('');
    setJsCode('');
    setAiPrompt('');
    setCreateDialogError(null);
  };

  const openCreateDialog = () => {
    resetFunctionForm();
    setCreateOpen(true);
  };

  const hydrateStaticFormFromStoredValue = (raw: string | null | undefined) => {
    if (raw == null || raw === '') {
      setValueType('STRING');
      setStaticValue('');
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (typeof parsed === 'string') {
        setValueType('STRING');
        setStaticValue(parsed);
        return;
      }
      if (typeof parsed === 'number') {
        setValueType('NUMBER');
        setStaticValue(String(parsed));
        return;
      }
      if (typeof parsed === 'boolean') {
        setValueType('BOOLEAN');
        setStaticValue(parsed ? 'true' : 'false');
        return;
      }
      setValueType('JSON');
      setStaticValue(JSON.stringify(parsed, null, 2));
    } catch {
      // Plain number / boolean / text stored without JSON quotes
      if (raw === 'true' || raw === 'false') {
        setValueType('BOOLEAN');
        setStaticValue(raw);
        return;
      }
      if (raw.trim() !== '' && Number.isFinite(Number(raw))) {
        setValueType('NUMBER');
        setStaticValue(raw);
        return;
      }
      setValueType('STRING');
      setStaticValue(raw);
    }
  };

  const openEditDialog = (func: FunctionDefinition) => {
    if (func.category !== 'Custom') return;
    const customName = func.name.startsWith('$') ? func.name.substring(1) : func.name;
    const custom = findCustomByName(customFunctions || [], customName);
    if (!custom) {
      setSnackbar({ open: true, message: 'Custom function not found', severity: 'error' });
      return;
    }
    setEditingFunction(custom);
    setNewName(custom.name);
    const isStatic = (custom.type || 'JS').toUpperCase() === 'STATIC';
    setFnType(isStatic ? 'STATIC' : 'JS');
    setAiPrompt('');
    if (isStatic) {
      hydrateStaticFormFromStoredValue(custom.value);
      setJsCode('');
    } else {
      setJsCode(custom.code || '');
      setValueType('STRING');
      setStaticValue('');
    }
    setCreateOpen(true);
  };

  const handleSaveFunction = async (event?: React.FormEvent) => {
    event?.preventDefault();
    setCreateDialogError(null);
    try {
      const trimmedName = newName.trim();
      if (!trimmedName) {
        setCreateDialogError('Function name is required');
        return;
      }

      if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(trimmedName)) {
        setCreateDialogError('Name must start with a letter and use only letters, numbers, or _');
        return;
      }

      const nameLower = trimmedName.toLowerCase();
      const nameTaken = (customFunctions || []).some(
        (f) =>
          f.name.toLowerCase() === nameLower &&
          (!editingFunction || f.id !== editingFunction.id)
      );
      if (nameTaken) {
        setCreateDialogError('You already have a function with this name');
        return;
      }

      if (!editingFunction && (customFunctions || []).length >= customFunctionLimit) {
        setCreateDialogError(
          `Custom function limit reached (${customFunctionLimit} per user). Delete one to create a new function.`,
        );
        return;
      }

      let staticPayloadValue: string | undefined;
      if (fnType === 'STATIC') {
        try {
          staticPayloadValue = buildStaticPayloadValue();
          if (!staticPayloadValue?.trim()) {
            setCreateDialogError('Value is required for a static function');
            return;
          }
        } catch (e: any) {
          setCreateDialogError(e?.message || 'Invalid value for static function');
          return;
        }
      } else {
        const trimmedCode = jsCode.trim();
        if (!trimmedCode || !trimmedCode.includes('return')) {
          setCreateDialogError('JavaScript function must include a return statement');
          return;
        }
      }

      const payload: Record<string, unknown> = {
        ...(editingFunction ? { id: editingFunction.id } : {}),
        name: trimmedName,
        type: fnType,
      };
      if (fnType === 'STATIC') {
        payload.value = staticPayloadValue;
      } else {
        payload.code = jsCode.trim();
      }

      setSavingFunction(true);
      const res = await apiService.post('/functions', payload);
      const saved = res?.data as CustomFunctionDto | undefined;

      if (saved?.id != null && saved?.name) {
        setCustomFunctions((prev) => {
          const merged = mergeSavedCustomFunction(prev, saved);
          primeCustomFunctionsCache(merged);
          return merged;
        });
      }

      setSnackbar({
        open: true,
        message: editingFunction ? 'Function updated successfully' : 'Function created successfully',
        severity: 'success',
      });

      setCreateOpen(false);
      resetFunctionForm();
      setExpandedCategory('Custom');
      void fetchCustomFunctions({ background: true });
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      const msg =
        typeof serverMessage === 'string'
          ? serverMessage
          : editingFunction
            ? 'Error updating function'
            : 'Error creating function';
      setCreateDialogError(msg);
      setSnackbar({
        open: true,
        message: msg,
        severity: 'error',
      });
    } finally {
      setSavingFunction(false);
    }
  };

  const handleDeleteCustomFunction = (func: FunctionDefinition) => {
    if (func.category !== 'Custom') return;
    setDeleteTarget(func);
  };

  const confirmDeleteCustomFunction = async () => {
    if (!deleteTarget) return;
    const func = deleteTarget;
    try {
      setDeleteInProgress(true);
      const customName = func.name.startsWith('$') ? func.name.substring(1) : func.name;
      const custom = findCustomByName(customFunctions || [], customName);
      if (!custom) {
        setSnackbar({ open: true, message: 'Custom function not found', severity: 'error' });
        return;
      }

      await apiService.delete(`/functions/${custom.id}`);
      invalidateCustomFunctionsCache();
      await fetchCustomFunctions();
      setSnackbar({ open: true, message: 'Function deleted successfully', severity: 'success' });
      setDeleteTarget(null);
    } catch (error: any) {
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      setSnackbar({
        open: true,
        message: typeof serverMessage === 'string' ? serverMessage : 'Error deleting function',
        severity: 'error',
      });
    } finally {
      setDeleteInProgress(false);
    }
  };

  return (
    <Box sx={functionsPageSx}>
      <Box sx={{ width: '100%', flexShrink: 0, overflow: 'visible' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: 1,
            minWidth: 0,
            px: { xs: 1.25, sm: 1.5 },
            pt: 1.25,
            pb: 0.5,
            minHeight: 36,
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{
              m: 0,
              fontWeight: 700,
              height: 36,
              lineHeight: '36px',
              letterSpacing: '-0.02em',
              color: DFT_BRAND.text,
              whiteSpace: 'nowrap',
            }}
          >
            Functions
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              minHeight: 36,
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<HelpOutlineIcon />}
              onClick={() => setHelpOpen(true)}
              sx={{
                ...functionsBtnSx,
                color: '#1d4ed8',
                borderColor: '#bfdbfe',
                bgcolor: '#ffffff',
                borderRadius: 0,
                px: 1.1,
                '&:hover': {
                  borderColor: '#93c5fd',
                  bgcolor: '#eff6ff',
                },
              }}
              aria-label="Open help for Functions"
            >
              Help
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={openCreateDialog}
              disabled={customFunctions.length >= customFunctionLimit}
              sx={{
                ...functionsBtnSx,
                borderRadius: 1.5,
                bgcolor: '#1d4ed8',
                px: 1.2,
                '&:hover': { bgcolor: '#1e40af' },
              }}
            >
              + Create Function
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={FUNCTIONS_WORKSPACE_SX}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search functions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              bgcolor: '#fff',
              height: 40,
              fontSize: '0.875rem',
              '& fieldset': { borderColor: DFT_BRAND.line },
            },
          }}
        />

      <Box sx={{ minWidth: 0 }}>
        {Object.entries(filteredFunctions).length === 0 ? (
          <Paper elevation={0} sx={{ ...functionsCardSx, p: { xs: 2.5, sm: 4 }, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No functions found matching "{searchQuery}"
            </Typography>
          </Paper>
        ) : (
          Object.entries(filteredFunctions).map(([category, functions]) => (
            <Accordion
              key={category}
              expanded={expandedCategory === category}
              onChange={handleAccordionChange(category)}
              sx={{ mb: 1.25, bgcolor: DFT_BRAND.paper }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 44,
                  bgcolor: DFT_BRAND.tint,
                  '&:hover': { bgcolor: DFT_BRAND.tint },
                  '& .MuiAccordionSummary-content': {
                    my: 0.75,
                    minWidth: 0,
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0, pr: 1 }}>
                  <Box
                    sx={{
                      width: 4,
                      height: 20,
                      bgcolor: getCategoryColor(category),
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.9375rem',
                      color: DFT_BRAND.text,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                    }}
                  >
                    {category}
                  </Typography>
                  <Chip
                    label={functions.length}
                    size="small"
                    sx={{
                      height: 22,
                      bgcolor: getCategoryColor(category),
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      flexShrink: 0,
                    }}
                  />
                  {category === 'Custom' && (
                    <Chip
                      label={`${customFunctions.length} / ${customFunctionLimit} used`}
                      size="small"
                      variant="outlined"
                      color={customFunctions.length >= customFunctionLimit ? 'warning' : 'default'}
                      sx={{
                        ml: 'auto',
                        mr: 0.5,
                        height: 22,
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        flexShrink: 0,
                        display: { xs: 'none', sm: 'inline-flex' },
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                {category === 'Custom' && customFunctionsLoading && customFunctions.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" color="text.secondary">
                      Loading your custom functions…
                    </Typography>
                  </Box>
                ) : category === 'Custom' && functions.length === 0 ? (
                  <Paper elevation={0} sx={{ ...functionsCardSx, p: 2, bgcolor: DFT_BRAND.tint }}>
                    {customFunctionsLoadError ? (
                      <>
                        <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                          {customFunctionsLoadError}
                        </Typography>
                        <Button size="small" variant="outlined" onClick={() => fetchCustomFunctions()} sx={functionsBtnSx}>
                          Retry
                        </Button>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No custom functions yet. Use <strong>+ Create Function</strong> to add JavaScript or static values you can reference as <code>$yourFunctionName</code> in requests.
                      </Typography>
                    )}
                  </Paper>
                ) : (
                <Grid container spacing={1.5}>
                  {functions.map((func) => (
                    <Grid item xs={12} lg={6} key={func.name}>
                      <Paper
                        elevation={0}
                        sx={{
                          ...functionsCardSx,
                          p: 2,
                          height: '100%',
                          '&:hover': {
                            borderColor: getCategoryColor(category),
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                fontSize: '1rem',
                                lineHeight: 1.3,
                                color: getCategoryColor(category),
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                              title={func.name}
                            >
                              {func.name}
                            </Typography>
                            <Tooltip title="Copy request reference">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyFunctionReference(func)}
                                sx={{ width: 32, height: 32, p: 0.5, flexShrink: 0 }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            <Tooltip title="Test function">
                              <IconButton
                                onClick={() => handleTest(func)}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  p: 0,
                                  bgcolor: '#1d4ed8',
                                  color: '#fff',
                                  borderRadius: '10px',
                                  '&:hover': { bgcolor: '#1e40af' },
                                }}
                                aria-label={`Test ${func.name}`}
                              >
                                <PlayArrowIcon sx={{ fontSize: 22 }} />
                              </IconButton>
                            </Tooltip>
                            {func.category === 'Custom' && (
                              <Tooltip title="Edit function">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => openEditDialog(func)}
                                  data-testid={`edit-custom-function-${func.name}`}
                                  sx={{ width: 32, height: 32, p: 0.5 }}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {func.category === 'Custom' && (
                              <Tooltip title="Delete function">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteCustomFunction(func)}
                                  sx={{ width: 32, height: 32, p: 0.5 }}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mb: 1.5,
                            fontSize: '0.875rem',
                            lineHeight: 1.45,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                          title={func.description}
                        >
                          {func.description}
                        </Typography>

                        <Box sx={{ mb: 1.25, minWidth: 0 }}>
                          <Typography sx={functionCodeLabelSx}>
                            Syntax
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              ...functionCodeValueSx,
                              bgcolor: DFT_BRAND.tint,
                              cursor: 'pointer',
                            }}
                            onClick={() => handleCopy(toRequestFunctionReference(func.syntax), 'Syntax')}
                            title={toRequestFunctionReference(func.syntax)}
                          >
                            {toRequestFunctionReference(func.syntax)}
                          </Paper>
                        </Box>

                        <Box sx={{ mb: 1.25, minWidth: 0 }}>
                          <Typography sx={functionCodeLabelSx}>
                            Example
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              ...functionCodeValueSx,
                              bgcolor: '#dbe8ff',
                            }}
                            title={toRequestFunctionReference(func.example)}
                          >
                            {toRequestFunctionReference(func.example)}
                          </Paper>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth={false}
        scroll="paper"
        sx={FUNCTIONS_DIALOG_SX}
        PaperProps={{ sx: FUNCTIONS_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ flexShrink: 0, py: 1.5, px: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HelpOutlineIcon sx={{ color: '#1d4ed8', fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DFT_BRAND.text }}>
              Using Functions
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 3.5 }}>
            Evaluated at runtime in URLs, bodies, headers, and data tables
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 2.5, py: 1.5, flex: 1, overflow: 'auto' }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Syntax
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, fontFamily: 'monospace', bgcolor: DFT_BRAND.tint, px: 1.25, py: 0.75, borderRadius: '10px', fontSize: '0.875rem' }}
              >
                $[name] &nbsp;·&nbsp; $[name](a, b)
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Examples
              </Typography>
              <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                {[
                  { label: 'URL', example: '/users/$[guid]' },
                  { label: 'Body', example: '{"email":"$[randomEmail]"}' },
                  { label: 'Table', example: '$[randomIntRange](1, 100)' },
                ].map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'baseline',
                      fontSize: '0.8125rem',
                      minWidth: 0,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 36, flexShrink: 0, color: 'text.secondary' }}>
                      {row.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', minWidth: 0 }}>
                      {row.example}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Tips
              </Typography>
              <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2, mt: 0.5, '& li': { mb: 0.35 } }}>
                <li>Press Play on a card to test before using in Collections.</li>
                <li>Fake Data functions give random values each run.</li>
                <li>Custom functions are yours only — max {customFunctionLimit} per user.</li>
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0, px: 2.5, py: 1.25 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => setHelpOpen(false)}
            sx={{ ...functionsBtnSx, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createOpen}
        onClose={() => {
          if (!savingFunction) {
            setCreateOpen(false);
            resetFunctionForm();
          }
        }}
        maxWidth={false}
        scroll="paper"
        sx={{
          ...FUNCTIONS_DIALOG_SX,
          zIndex: FUNCTION_CREATE_DIALOG_Z,
        }}
        PaperProps={{ sx: FUNCTIONS_DIALOG_PAPER_SX }}
        disableScrollLock
      >
        <DialogTitle sx={{ flexShrink: 0, py: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DFT_BRAND.text }}>
            {editingFunction ? 'Edit Function' : 'Create Function'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Referenced as <strong>$[{newName.trim() || 'name'}]</strong> in requests
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 2.5, py: 1.5, flex: 1, overflow: 'auto' }}>
          <Box
            component="form"
            id="custom-function-form"
            onSubmit={handleSaveFunction}
            noValidate
          >
            <Stack spacing={1.5}>
              {createDialogError && (
                <Alert severity="error" sx={{ py: 0.25 }} onClose={() => setCreateDialogError(null)}>
                  {createDialogError}
                </Alert>
              )}
              <TextField
              label="Function name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              size="small"
              placeholder="myToken"
              helperText="Letters and numbers — no spaces or $"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="fn-type-label">Type</InputLabel>
              <Select
                labelId="fn-type-label"
                label="Type"
                value={fnType}
                onChange={(e) => setFnType(e.target.value as 'STATIC' | 'JS')}
                MenuProps={FUNCTION_DIALOG_SELECT_MENU_PROPS}
              >
                <MenuItem value="STATIC">Static value</MenuItem>
                <MenuItem value="JS">JavaScript</MenuItem>
              </Select>
            </FormControl>

            {fnType === 'STATIC' && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel id="value-type-label">Value type</InputLabel>
                  <Select
                    labelId="value-type-label"
                    label="Value type"
                    value={valueType}
                    onChange={(e) =>
                      setValueType(e.target.value as 'STRING' | 'NUMBER' | 'BOOLEAN' | 'JSON')
                    }
                    MenuProps={FUNCTION_DIALOG_SELECT_MENU_PROPS}
                  >
                    <MenuItem value="STRING">String</MenuItem>
                    <MenuItem value="NUMBER">Number</MenuItem>
                    <MenuItem value="BOOLEAN">Boolean</MenuItem>
                    <MenuItem value="JSON">JSON</MenuItem>
                  </Select>
                </FormControl>
                {valueType === 'BOOLEAN' ? (
                  <FormControl fullWidth size="small">
                    <InputLabel id="bool-val-label">Value</InputLabel>
                    <Select
                      labelId="bool-val-label"
                      label="Value"
                      value={staticValue === 'true' ? 'true' : 'false'}
                      onChange={(e) => setStaticValue(e.target.value)}
                      MenuProps={FUNCTION_DIALOG_SELECT_MENU_PROPS}
                    >
                      <MenuItem value="true">true</MenuItem>
                      <MenuItem value="false">false</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    label={valueType === 'JSON' ? 'JSON value' : 'Value'}
                    value={staticValue}
                    onChange={(e) => setStaticValue(e.target.value)}
                    fullWidth
                    multiline={valueType === 'JSON'}
                    minRows={valueType === 'JSON' ? 3 : 1}
                    placeholder={
                      valueType === 'JSON'
                        ? '{"key":"value"}'
                        : valueType === 'NUMBER'
                          ? '42'
                          : 'default-value'
                    }
                    size="small"
                  />
                )}
              </>
            )}

            {fnType === 'JS' && (
              <>
                <TextField
                  label="AI prompt (optional)"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  size="small"
                  placeholder="Describe what the function should do"
                />
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{ alignSelf: 'flex-start', ...functionsBtnSx, px: 1.2 }}
                  startIcon={generatingCode ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
                  onClick={handleGenerateCode}
                  disabled={generatingCode || !aiPrompt.trim() || savingFunction}
                >
                  {generatingCode ? 'Generating…' : 'Generate with AI'}
                </Button>
                <TextField
                  label="Code"
                  value={jsCode}
                  onChange={(e) => setJsCode(e.target.value)}
                  fullWidth
                  multiline
                  minRows={5}
                  size="small"
                  placeholder="return String(args[0] || '').toUpperCase();"
                  helperText="args[0], args[1], … — use return"
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.8125rem' } }}
                />
              </>
            )}
            </Stack>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ flexShrink: 0, px: 2.5, py: 1.25, gap: 1 }}>
          <Button
            size="small"
            type="button"
            onClick={() => {
              setCreateOpen(false);
              resetFunctionForm();
            }}
            disabled={savingFunction}
            sx={functionsBtnSx}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            form="custom-function-form"
            variant="contained"
            disabled={savingFunction}
            sx={{ ...functionsBtnSx, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
          >
            {savingFunction ? 'Saving…' : editingFunction ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteInProgress && setDeleteTarget(null)}
        maxWidth={false}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
          '& .MuiDialog-container': {
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
          },
        }}
        PaperProps={{ sx: FUNCTIONS_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700, fontSize: '1.05rem' }}>
          Delete function
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.875rem' }}>
            Delete function &quot;{deleteTarget?.name}&quot;?
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: '0.875rem' }} color="text.secondary">
            This function is shared across your organization. Requests using{' '}
            {`$[${
              deleteTarget?.name?.startsWith('$')
                ? deleteTarget.name.substring(1)
                : deleteTarget?.name ?? ''
            }]`}{' '}
            may stop working after deletion.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteInProgress} sx={functionsBtnSx}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDeleteCustomFunction}
            disabled={deleteInProgress}
            data-testid="confirm-delete-custom-function"
            sx={functionsBtnSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(testResultDialog)}
        onClose={() => setTestResultDialog(null)}
        maxWidth={false}
        sx={FUNCTIONS_DIALOG_SX}
        PaperProps={{ sx: FUNCTIONS_DIALOG_PAPER_SX }}
      >
        <DialogTitle sx={{ pb: 1.25, fontWeight: 700, fontSize: '1.05rem', color: DFT_BRAND.text, pr: 2, wordBreak: 'break-word' }}>
          {testResultDialog?.name} executed successfully!
        </DialogTitle>
        <DialogContent>
          <Typography sx={functionCodeLabelSx}>
            Test Result
          </Typography>
          <Paper
            elevation={0}
            sx={{
              ...functionCodeValueSx,
              bgcolor: DFT_BRAND.methodBg,
              color: DFT_BRAND.methodColor,
            }}
          >
            {testResultDialog?.result}
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 1 }}>
          <Button
            onClick={() => {
              if (testResultDialog?.result) {
                handleCopy(testResultDialog.result, 'Test result');
              }
            }}
            sx={functionsBtnSx}
          >
            Copy
          </Button>
          <Button
            variant="contained"
            onClick={() => setTestResultDialog(null)}
            sx={{ ...functionsBtnSx, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Functions;
